import React, { useCallback, useEffect } from "react";
import Loading from "../../components/Loading/Loading";
import { Helmet } from "react-helmet";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAirport } from "../../AirportContext";
import PageLayout from "../../components/PageLayout";
import useAirportFetch from "../../hooks/useAirportFetch";
import useCalculateActiveRunways from "../../hooks/useCalculateActiveRunways";
import AirportHeader from "./AirportHeader";
import AirportRunways from "./AirportRunways";
import AirportRunwaysMap from "./AirportRunwaysMap";
import WeatherDashboard from "./WeatherDashboard";
import WindDirectionBackground from "./WindDirectionBackground";
import Compass from "./Compass";

export default function AirportPage() {
  const { t } = useTranslation();
  const [airport, setAirport] = useAirport();
  const { params } = useRouteMatch();
  const history = useHistory();

  const airportIsValid =
    airport && airport.icao.toLowerCase() === params?.icao.toLowerCase();

  const handleError = useCallback(
    (error) => {
      if (error !== null) {
        history.replace("/", { error, icao: params?.icao });
      }
    },
    [history, params]
  );

  const fetchAirport = useAirportFetch({
    onLoaded: setAirport,
    onError: handleError,
  });

  useEffect(() => {
    if (!airportIsValid) {
      fetchAirport(params?.icao);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeRunwaysData = useCalculateActiveRunways(airport, airportIsValid);

  if (!airportIsValid) return <Loading />;

  return (
    <PageLayout>
      <Helmet>
        <title>
          {airport.name} · {airport.icao} · {t("app.name")}
        </title>
        <meta
          name="description"
          content={`${airport.name} (${airport.icao}) — METAR, ${t("airport.wind")}, ${t("airport.runways")}`}
        />
      </Helmet>

      {/* Wind direction atmosphere */}
      <WindDirectionBackground windDirection={airport.wind_direction} />

      {/* Compass indicator */}
      <Compass />

      {/* Page content */}
      <div className="px-5 md:px-10 lg:px-20 mx-auto max-w-screen-xl w-full py-8">
        <AirportHeader airport={airport} />
        <WeatherDashboard airport={airport} />
        <AirportRunways
          airport={airport}
          activeRunwaysData={activeRunwaysData}
        />
        <AirportRunwaysMap
          airport={airport}
          activeRunwaysData={activeRunwaysData}
        />
      </div>
    </PageLayout>
  );
}
